import React, { FunctionComponent } from "react";

import DeletedFileIcon from "@uui/FileX03";
import LoadingIcon from "@uui/Loading02";

import Typography from "@mui/material/Typography";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import useEmailPreferences from "@app/analytics/account/UserPreferences/EmailPreferencesModule/useEmailPreferences";
import { BaseOuterContainer } from "@app/analytics/components/reports/styles";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useAuth from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";
import type { BillingCurrentPlan } from "@app/common/types";
import type { User } from "@app/common/types";

import Button from "@rui/Button";
import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import { Link } from "@rui/Link";
import Stack from "@rui/Stack";

import {
  ContentContainer,
  MeetingRecapCTA,
  OverlayOuterContainer,
  StarIcon,
} from "./styles";
import MeetingReportHeader, {
  MeetingReportHeaderProps,
} from "../MeetingReportHeader";

interface EmptyMeetingReportProps extends MeetingReportHeaderProps {
  type: "data_deleted" | "processing" | "upgrade_processing";
}

type EmptyStateComponent = FunctionComponent<{
  user: User | null;
  plan: BillingCurrentPlan;
}>;

const MeetingReportProcessing: EmptyStateComponent = ({ user }) => {
  const { emailSubscriptions, emailPreferencesLoading } =
    useEmailPreferences(user);

  const showMeetingRecapCTA =
    Boolean(user) &&
    !emailPreferencesLoading &&
    !emailSubscriptions.includes("Meeting Recaps");

  return (
    <OverlayOuterContainer>
      <EmptyStateContainer>
        <ContentContainer>
          <Stack spacing={1} variant="center">
            <EmptyStateIcon Icon={LoadingIcon} />
            <EmptyStateTitle>Report processing</EmptyStateTitle>
            <EmptyStateSubtext sx={{ pb: 0 }}>
              Good things take time, including this Meeting Report.
            </EmptyStateSubtext>
            <EmptyStateSubtext>
              Depending on the length of the meeting, the report will be
              available 5 to 30 minutes after the meeting.
            </EmptyStateSubtext>
          </Stack>
          <Link to={ROUTES.MEETING_REPORTS}>
            <Button variant="gradient">Back to all Reports</Button>
          </Link>
        </ContentContainer>
        {showMeetingRecapCTA && (
          <MeetingRecapCTA>
            <Stack direction="row" spacing={1} alignItems="center">
              <StarIcon />
              <Typography variant="bodySecondaryMed">
                Get meeting reports delivered to your inbox
              </Typography>
            </Stack>
            <Typography variant="bodySecondaryReg">
              After a meeting, Read can automatically send you a recap email as
              soon as the report is done processing.{" "}
              <Link
                to={ROUTES.USER_SETTINGS_REPORTING}
                sx={{ fontWeight: 500, color: "shades.primary.500" }}
              >
                Enable email recaps
              </Link>{" "}
              and let Read handle the rest!
            </Typography>
          </MeetingRecapCTA>
        )}
      </EmptyStateContainer>
    </OverlayOuterContainer>
  );
};

const UpgradeTriggeredProcessing: EmptyStateComponent = ({ plan }) => (
  <OverlayOuterContainer>
    <EmptyStateContainer>
      <EmptyStateIcon Icon={LoadingIcon} />
      <EmptyStateTitle>Read is generating this report</EmptyStateTitle>
      <EmptyStateSubtext>
        Good news!
        {isFreePlan(plan.type)
          ? " A participant upgraded to Read Pro or Enterprise, so "
          : ` Since you upgraded to ${plan.label}, `}
        you'll have access to this report shortly. Check back in a bit!
      </EmptyStateSubtext>
      <Link to={ROUTES.MEETING_REPORTS}>
        <Button variant="gradient">Back to Meeting Reports</Button>
      </Link>
    </EmptyStateContainer>
  </OverlayOuterContainer>
);

const DataDeleted: EmptyStateComponent = () => (
  <OverlayOuterContainer>
    <EmptyStateContainer>
      <EmptyStateIcon Icon={DeletedFileIcon} />
      <EmptyStateTitle>Report data has been deleted</EmptyStateTitle>
      <EmptyStateSubtext>
        Unprocessed report data is retained for 30 days, then automatically
        deleted.
      </EmptyStateSubtext>
      <Link to={ROUTES.MEETING_REPORTS}>
        <Button variant="gradient">Back to Meeting Reports</Button>
      </Link>
    </EmptyStateContainer>
  </OverlayOuterContainer>
);

const CONTENT_BY_TYPE: {
  [key in EmptyMeetingReportProps["type"]]: EmptyStateComponent;
} = {
  data_deleted: DataDeleted,
  processing: MeetingReportProcessing,
  upgrade_processing: UpgradeTriggeredProcessing,
};

const EmptyMeetingReport = ({
  type,
  ...headerProps
}: EmptyMeetingReportProps) => {
  const { user } = useAuth();
  const { currentPlan } = useBillingContext();

  const Content = CONTENT_BY_TYPE[type];

  return (
    <>
      <BaseOuterContainer>
        <MeetingReportHeader {...headerProps} />
        <Content user={user} plan={currentPlan} />
      </BaseOuterContainer>
    </>
  );
};

export default EmptyMeetingReport;
